import moment from 'moment';
import {
  createFilterparameters,
  convertDurationToBin,
  abortableFetch
} from './pollTools.js';
import { isLoggedIn, isAdmin } from '../helpers/authentication.js';
import { DISPLAYMODE_PARK } from '../reducers/layers.js';
import {shouldFetchVehicles} from './pollTools.js';
import { isInNetherlands } from '../helpers/netherlands';

var store_parkingdata;
var timerid_parkingdata;

// theFetch: Variabele used for managing fetch calls
let theFetch = null;

// URL of the request currently in flight, so an identical request can reuse
// it instead of aborting and starting over (see doApiCall)
let theFetchUrl = null;

// Variable to keep track of vehicles response
// Only do a new fetch() if needed
let activeVehicles;

// Variable to keep track of filter changes
// Only do a new fetch() if needed
let existingFilter;

const processVehiclesResult = (state, vehicles) => {
  activeVehicles = vehicles;

  let geoJson = {
   "type":"FeatureCollection",
   "features":[]
  }
  
  let operatorcolors = {};
  let operatorstats = {}
  state.metadata.aanbieders.forEach(o => {
    operatorcolors[o.system_id || o.value]=o.color;
    operatorstats[o.system_id || o.value]=0;
  });

  var start_time = moment(state.filter.datum);
  const start_time_ms = start_time.valueOf();

  // Get list of providers to exclude
  const aanbiedersexclude = state.filter.aanbiedersexclude.split(",") || [];
  // Get parkeerduur length to exclude
  const parkeerduurexclude = state.filter.parkeerduurexclude.split(",") || [];
  const showOnlyNonOperational = state.filter.non_operational_only === true;
  const loggedIn = isLoggedIn(state);

  vehicles.forEach(v => {
    let in_public_space_since = loggedIn ? v.start_time : v.in_public_space_since;

    // Plain Date math instead of constructing a moment per vehicle (~10x
    // faster over 25k+ vehicles); fall back to moment for exotic date formats
    let since_ms = in_public_space_since ? Date.parse(in_public_space_since) : Date.now();
    if(isNaN(since_ms) && in_public_space_since) {
      since_ms = moment(in_public_space_since).valueOf();
    }
    var minutes = Math.floor((start_time_ms - since_ms) / 60000);
    const duration_bin = convertDurationToBin(minutes);

    let feature = {
       "type":"Feature",
       "properties":{
          "id":v.location.latitude+","+v.location.longitude,
          "vehicle_id":v.bike_id,
          "system_id": v.system_id || v.value,// v.value is used in the public map
          "form_factor": v.form_factor || null,
          "in_public_space_since": in_public_space_since,
          "duration_bin": duration_bin,
          "is_non_operational": v.is_non_operational === true,
       },
       "geometry":{
          "type":"Point",
          "coordinates":[
             v.location.longitude,
             v.location.latitude,
             0.0
          ]
       }
    }

    operatorstats[v.system_id || v.value]+=1;

    // Filter markers
    let markerVisible = ! loggedIn || !parkeerduurexclude.includes(duration_bin.toString());
    markerVisible = markerVisible && (aanbiedersexclude.includes(v.system_id || v.value) === false)
    if (showOnlyNonOperational) {
      markerVisible = markerVisible && v.is_non_operational === true;
    }
    if(markerVisible) {
      geoJson.features.push(feature);
    }
  })
  if(process && process.env.DEBUG) console.log('geoJson in pollParkingData', geoJson)

  // Save vehicles in store
  store_parkingdata.dispatch({
    type: 'SET_VEHICLES',
    payload: geoJson
  })

  // Update operator stats (= number of vehicles per operator) in store
  store_parkingdata.dispatch({
    type: 'SET_VEHICLES_OPERATORSTATS',
    payload: operatorstats
  })
}

const doApiCall = (
  state,
  callback
) => {

  const canfetchdata = state && isLoggedIn(state)  && state.filter && state.authentication.user_data.token;
  const is_admin = isAdmin(state);

  // Set API URL
  let url = `${process.env.REACT_APP_MAIN_API_URL}/dashboard-api/public/vehicles_in_public_space`;

  let options = {};
  let filterparams = createFilterparameters(DISPLAYMODE_PARK, state.filter, state.metadata, {
    show_global: is_admin,
    is_logged_in: isLoggedIn(state)
  });

  // Set query params for guests
  if(! canfetchdata) {
    if(filterparams.length>0) {
      url += "?" + filterparams.join("&");
    }
  }

  // Set query params for logged in users
  else {
    if (canfetchdata) {
      url = `${process.env.REACT_APP_MAIN_API_URL}/dashboard-api/park_events`;
      if (filterparams.length > 0) {
        url += "?" + filterparams.join("&");
      }
      options = {
        headers: { authorization: "Bearer " + state.authentication.user_data.token }
      };
    }
  }
  
  // If a request for this exact URL is already in flight, let it finish
  // instead of aborting and re-issuing it: the response is processed with the
  // then-current store state, so the result is the same either way.
  if(theFetch && theFetchUrl === url) {
    return;
  }

  // Start loading
  store_parkingdata.dispatch({type: 'SHOW_LOADING', payload: true});

  // Abort previous fetch
  // Info:
  // - https://stackoverflow.com/q/63412985
  // - https://davidwalsh.name/cancel-fetch
  if(theFetch) {
    theFetch.abort();
  }
  // Now do a new fetch
  const thisFetch = abortableFetch(url, options);
  theFetch = thisFetch;
  theFetchUrl = url;

  // Only clear the in-flight tracking if it still points at this request
  // (a newer request may have replaced it in the meantime)
  const clearFetchTracking = () => {
    if(theFetch === thisFetch) {
      theFetch = null;
      theFetchUrl = null;
    }
  };

  thisFetch.ready.then(function(response) {
    if(! response.ok) {
      clearFetchTracking();
      console.error("unable to fetch: %o", response);
      return false
    }

    response.json().then(function(vehicles) {
      if(isLoggedIn(state)) {
        vehicles = vehicles.park_events
      } else {
        // The public map only covers the Netherlands
        vehicles = (vehicles.vehicles_in_public_space || []).filter(v => {
          return v.location && isInNetherlands(v.location.longitude, v.location.latitude);
        });
      }
      // Process with the *current* store state (not the state at request
      // time), so client-side filters that changed while the request was in
      // flight are applied to the result.
      callback(store_parkingdata.getState(), vehicles);
    }).catch(ex=>{
      console.error("unable to decode JSON");
    }).finally(()=>{
      clearFetchTracking();
      // Stop loading
      store_parkingdata.dispatch({type: 'SHOW_LOADING', payload: false});
    })
  }).catch(ex=>{
    // If this request was aborted because a newer one replaced it, leave the
    // loading state to the newer request
    if(theFetch !== thisFetch) return;
    clearFetchTracking();
    // Stop loading
    store_parkingdata.dispatch({type: 'SHOW_LOADING', payload: false});
    console.error("fetch error - unable to fetch JSON from %s", url);
  });

}

const updateParkingData = async () => {
  try {
    if(undefined===store_parkingdata) {
      if(process && process.env.DEBUG) console.error("no redux state available yet - skipping zones update");
      return false;
    }
    
    // Wait for zone data
    const state = store_parkingdata.getState();
    if(! state) return;

    // const canfetchdata = state && isLoggedIn(state)  && state.filter && state.authentication.user_data.token;

    // Should we (re)fetch vehicles?
    const doFetchVehicles = shouldFetchVehicles(state.filter, existingFilter);

    // Update active filter
    existingFilter = state.filter;

    if(doFetchVehicles || (! activeVehicles && ! theFetch)) {
      doApiCall(state, processVehiclesResult);
    } else if(activeVehicles) {
      // Regenerate geoJson without querying API
      processVehiclesResult(state, activeVehicles);
    }
    // else: no vehicles yet but a fetch is in flight; its callback processes
    // the response with the store state at completion time

  } catch(ex) {
    console.error("Unable to update zones", ex)
  } finally {
    //:)
  }
}

export const initUpdateParkingData = (_store) => {
  store_parkingdata = _store;
  if(! store_parkingdata) { console.log('No store yet.'); return; }
  if(timerid_parkingdata) { clearTimeout(timerid_parkingdata); }
  updateParkingData();
}
